import { checkFulfillmentCanFileClaim, checkFulfillmentItemCanFileClaim } from '@helpers';
import { CREDIT_BONUS_TYPES, ClaimCategory } from '@pulse/constants/claims';
import { ClaimModel } from '@pulse/models';
import OrderService from '@pulse/services/OrderService';
import dayjs from 'dayjs';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import { useClaimStore } from '~/store/claim';

export const useOrdersStore = defineStore('conciergeOrders', () => {
  const currentOrder = ref(null);
  const claim = useClaimStore();
  const { currentShipmentId, claimCategory } = storeToRefs(claim);
  const config = useRuntimeConfig();
  const service = new OrderService(config.public.apiBaseUrl);
  const selectedItems = ref([]);
  const itemEligibility = ref({});
  const orderItems = computed(() => currentOrder.value.order.orderItems || []);

  async function find(email, orderNumber, storeId) {
    currentOrder.value = await service.find(email, orderNumber, storeId);

    claim.setCustomer({
      customerName: currentOrder.value.customerName,
      email,
      sourceOrderId: orderNumber,
    });

    if (currentOrder.value?.systemOrderId) {
      await claim.findClaimSession(currentOrder.value.systemOrderId);
    }

    itemEligibility.value = currentOrder.value.eligibility || {};

    return currentOrder.value;
  }

  function reset() {
    currentOrder.value = null;
  }

  async function fetchOrder(email, sourceOrderId) {
    const dbOrder = await find(email, sourceOrderId);

    if (!dbOrder) return;

    currentOrder.value = dbOrder;
  }

  function setOrderDetails(details) {
    currentOrder.value = { ...currentOrder.value, ...details };
  }

  function getClaimedQuantity(sourceItemId) {
    const claimItems = shippingClaims.value
      .flatMap(claim => claim.claimItems)
      .filter(claimItem => claimItem.sourceItemId === sourceItemId);

    return claimItems.reduce((acc, claimItem) => acc + claimItem.quantity, 0);
  }

  const claimFeedClaims = computed(() => {
    if (!currentOrder.value.existingClaims) {
      return [];
    }

    return ClaimModel.fromArrayExtending(currentOrder.value?.existingClaims, {
      order: currentOrder.value,
    });
  });

  const formattedCreditBonus = computed(() => {
    if (currentOrder.value.settings.credit.bonus === null) return null;

    return currentOrder.value.settings.credit.bonusType === CREDIT_BONUS_TYPES.fixed
      ? new Intl.NumberFormat(navigator.language, {
          currency: currentOrder.value.currency,
          style: 'currency',
        }).format(currentOrder.value.settings.credit.bonus * currentOrder.value.exchangeRate)
      : `${currentOrder.value.settings.credit.bonus}%`;
  });

  const soonestFileDate = computed(() => {
    const fulfillments = currentOrder.value?.fulfillments;
    if (!fulfillments) return null;

    const [date] = fulfillments.map(({ eligibleAt }) => eligibleAt).sort();

    return date;
  });

  const latestFileDate = computed(() => {
    return currentOrder.value.fulfillments
      .map(({ uneligibleAt }) => uneligibleAt)
      .sort()
      .at(-1);
  });

  const eligibleFulfillments = computed(() => {
    return currentOrder.value.fulfillments.filter(({ eligible }) => eligible);
  });

  const tooSoonToFile = computed(() => {
    if (currentOrder.value.fulfillments.length === 0) {
      return false;
    }

    return currentOrder.value.fulfillments.every(
      ({ eligibilityCode, eligible }) => !eligible && eligibilityCode !== 'expired',
    );
  });

  /**
   * TODO - add logic
   */
  const showTooLateAlert = computed(() => {
    const order = currentOrder?.value;
    if (order?.protection?.warranty || !order?.protection?.shipping) return false;

    return outsideShippingWindow.value;
  });

  const outsideShippingWindow = computed(() => {
    return (
      currentOrder?.value.fulfillments[0].eligible === false &&
      currentOrder?.value.fulfillments[0].eligibilityCode === 'expired'
    );
  });
  const eligibleForClaim = computed(() => {
    if (!currentOrder.value) return false;

    return currentOrder.value.protected || currentOrder?.value?.protection?.warranty;
  });

  const eligibleForShippingClaim = computed(() => {
    if (!currentOrder.value) return false;

    return currentOrder.value?.protection?.shipping;
  });

  const eligibleForWarrantyClaim = computed(() => {
    if (!currentOrder.value) return false;

    return currentOrder.value?.protection?.warranty;
  });

  const allCurrentOrderItems = computed(() => {
    if (!currentOrder.value) return [];

    return currentOrder.value.fulfillments.reduce((all, fulfillment) => {
      return all.concat(
        fulfillment.items.map(({ item, quantity }) => ({
          ...item,
          quantity,
          sourceFulfillmentId: fulfillment.sourceFulfillmentId,
        })),
      );
    }, []);
  });

  const deliveredFulfillments = computed(() => {
    if (!currentOrder.value) return [];

    return currentOrder.value.fulfillments.filter(({ fulfillmentStatus }) => fulfillmentStatus === 'DELIVERED');
  });

  const hasDeliveredFulfillments = computed(() => {
    return deliveredFulfillments.value.length > 0;
  });

  const currentShipmentOrderItems = computed(() => {
    if (currentOrder?.value?.fulfillments?.length < 1 || !currentShipmentId?.value) {
      return [];
    }

    return currentOrder.value.fulfillments
      .find(({ sourceFulfillmentId }) => sourceFulfillmentId === currentShipmentId.value)
      .items.map(({ item, quantity, sourceFulfillmentId }) => ({
        ...item,
        quantity,
        sourceFulfillmentId,
      }));
  });

  const originalAddress = computed(() => {
    return currentOrder.value?.shippingAddress;
  });

  const setOriginalAddress = address => {
    currentOrder.value.shippingAddress = address;
  };

  const orderHasShipped = computed(() => {
    return currentOrder.value.fulfillments.length > 0;
  });

  const isOta = computed(() => {
    return currentOrder.value.isOneTimeAppeasement;
  });

  const supportsReturns = computed(() => {
    return currentOrder.value && !!currentOrder.value.settings?.capabilities?.returns;
  });

  const supportsWarranties = computed(() => {
    return currentOrder.value && !!currentOrder.value.settings?.capabilities?.warranties;
  });

  const returns = computed(() => {
    return currentOrder.value?.returns ?? [];
  });

  const claims = computed(() => {
    return currentOrder?.value?.existingClaims ?? [];
  });

  const shippingClaims = computed(() => {
    return currentOrder?.value?.existingClaims.filter(c => c.claimCategory === ClaimCategory.SHIPPING) ?? [];
  });

  const hasEligibleItems = computed(() => {
    return Object.values(itemEligibility.value).some(value => value.shipping > 0);
  });

  const hasEligibleWarrantyItems = computed(() => {
    return Object.values(itemEligibility.value).some(value => value.warranty.qty > 0);
  });

  const returnsUrl = computed(() => {
    return currentOrder.value.settings.returns.portalUrl;
  });

  const trackingUrl = computed(() => {
    return currentOrder.value?.settings?.returns?.trackingUrl;
  });

  const canFileClaim = computed(() => {
    if (!eligibleForClaim.value || !eligibleForShippingClaim.value) return false;

    const canFileClaimOnAtleastOneFulfillment = eligibleFulfillments.value.some(fulfillment => {
      return checkFulfillmentCanFileClaim(fulfillment, claims.value);
    });

    return canFileClaimOnAtleastOneFulfillment;
  });

  const capabilities = computed(() => {
    return currentOrder.value.settings?.capabilities;
  });

  function getWarrantyItems(filterFn) {
    return orderItems.value.flatMap(orderItem => {
      const eligibilityEntry = itemEligibility.value[orderItem.sourceItemId];

      if (!eligibilityEntry) return [];

      const { expires, validity, qty } = eligibilityEntry.warranty || {};

      if (!expires || !filterFn(dayjs(expires), qty)) return [];

      //prices and quantites are hidden for warranty items in concierge
      return [
        {
          ...orderItem,
          expires,
          price: undefined,
          quantity: undefined,
          validity,
        },
      ];
    });
  }

  const eligibleWarrantyItems = computed(() => getWarrantyItems((expires, qty) => expires.isAfter(dayjs()) && qty > 0));

  const ineligibleWarrantyItems = computed(() =>
    getWarrantyItems((expires, qty) => expires.isBefore(dayjs()) || qty <= 0),
  );

  const eligibleShippingItems = computed(() => {
    return currentShipmentOrderItems.value.filter(item => {
      return (
        checkFulfillmentItemCanFileClaim(shippingClaims.value, item.sourceItemId, item.quantity) &&
        itemEligibility.value[item.sourceItemId] &&
        itemEligibility.value[item.sourceItemId].shipping > 0
      );
    });
  });

  const ineligibleShippingItems = computed(() => {
    return currentShipmentOrderItems.value.filter(item =>
      eligibleItems.value.every(eItem => eItem.sourceItemId !== item.sourceItemId),
    );
  });

  const ineligibleItems = computed(() => {
    return claimCategory.value === ClaimCategory.WARRANTY
      ? ineligibleWarrantyItems.value
      : ineligibleShippingItems.value;
  });

  const eligibleItems = computed(() => {
    return claimCategory.value === ClaimCategory.WARRANTY ? eligibleWarrantyItems.value : eligibleShippingItems.value;
  });

  const claimSubtypes = computed(() => {
    return currentOrder.value?.claimSubtypes || [];
  });

  return {
    allCurrentOrderItems,
    canFileClaim,
    capabilities,
    claimFeedClaims,
    claimSubtypes,
    claims,
    currentOrder,
    currentShipmentOrderItems,
    deliveredFulfillments,
    eligibleForClaim,
    eligibleForShippingClaim,
    eligibleForWarrantyClaim,
    eligibleFulfillments,
    eligibleItems,
    eligibleShippingItems,
    eligibleWarrantyItems,
    fetchOrder,
    find,
    formattedCreditBonus,
    getClaimedQuantity,
    hasDeliveredFulfillments,
    hasEligibleItems,
    hasEligibleWarrantyItems,
    ineligibleItems,
    ineligibleShippingItems,
    ineligibleWarrantyItems,
    isOta,
    itemEligibility,
    latestFileDate,
    orderHasShipped,
    originalAddress,
    outsideShippingWindow,
    reset,
    returns,
    returnsUrl,
    selectedItems,
    setOrderDetails,
    setOriginalAddress,
    shippingClaims,
    showTooLateAlert,
    soonestFileDate,
    supportsReturns,
    supportsWarranties,
    tooSoonToFile,
    trackingUrl,
  };
});
